import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Users, Loader2, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventsApi, Event } from '../services/eventsApi';
import { AuthPromptModal } from '../components/AuthPromptModal';
import { useAuthGate } from '../hooks/useAuthGate';
import { appPath } from '../routes/appPaths';

const EVENTS_STAGGER_KEY = 'theories_stagger_events';

function shouldPlayEventsStagger(): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EVENTS_STAGGER_KEY) !== '1';
  } catch {
    return true;
  }
}

export default function EventsScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isAuthed,
    userId,
    requireAuth,
    authPromptOpen,
    closeAuthPrompt,
    confirmAuthPrompt,
  } = useAuthGate();

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.getAll(),
  });

  const playEventsStagger = shouldPlayEventsStagger();

  useEffect(() => {
    if (!playEventsStagger || events.length === 0) return;
    const ms = Math.min(events.length * 40 + 400, 2500);
    const t = window.setTimeout(() => {
      try {
        sessionStorage.setItem(EVENTS_STAGGER_KEY, '1');
      } catch {
        /* ignore */
      }
    }, ms);
    return () => clearTimeout(t);
  }, [playEventsStagger, events.length]);

  const { data: registrations = [] } = useQuery({
    queryKey: ['user-registrations', userId],
    queryFn: () => eventsApi.getUserRegistrations(userId!),
    enabled: isAuthed && !!userId,
  });

  const registeredIds = new Set(registrations.map((r) => r.eventId));

  const registerMutation = useMutation({
    mutationFn: (eventId: string) => eventsApi.register(userId!, eventId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-registrations', userId] }),
  });

  const unregisterMutation = useMutation({
    mutationFn: (eventId: string) => eventsApi.unregister(userId!, eventId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-registrations', userId] }),
  });

  function requireAuthThen(action: () => void, eventId: string) {
    requireAuth(appPath(`/events/${eventId}`), action);
  }

  const today = new Date().toISOString().split('T')[0];
  const todayEvents = events.filter((e) => e.date.split('T')[0] === today);
  const futureEvents = events
    .filter((e) => e.date.split('T')[0] > today)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'rgba(255,255,255,0.35)' }} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: '#0a0a0a',
        color: '#fff',
        paddingBottom: 'calc(max(16px, env(safe-area-inset-bottom, 0px)) + 96px)',
      }}
    >
      <div className="max-w-md mx-auto w-full px-4 pt-6">
        <div className="mb-6">
          <h1
            className="text-2xl font-bold uppercase"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
          >
            События
          </h1>
        </div>

        {todayEvents.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              Сегодня
            </h2>
            <div className="space-y-4">
              {todayEvents.map((event, i) =>
                playEventsStagger ? (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <EventCard
                      event={event}
                      isRegistered={registeredIds.has(event.id)}
                      isPending={
                        (registerMutation.isPending && registerMutation.variables === event.id) ||
                        (unregisterMutation.isPending && unregisterMutation.variables === event.id)
                      }
                      onRegister={() => requireAuthThen(() => registerMutation.mutate(event.id), event.id)}
                      onUnregister={() => requireAuthThen(() => unregisterMutation.mutate(event.id), event.id)}
                      guestLabel={!isAuthed}
                      onDetail={() => navigate(appPath(`/events/${event.id}`))}
                    />
                  </motion.div>
                ) : (
                  <EventCard
                    key={event.id}
                    event={event}
                    isRegistered={registeredIds.has(event.id)}
                    isPending={
                      (registerMutation.isPending && registerMutation.variables === event.id) ||
                      (unregisterMutation.isPending && unregisterMutation.variables === event.id)
                    }
                    onRegister={() => requireAuthThen(() => registerMutation.mutate(event.id), event.id)}
                    onUnregister={() => requireAuthThen(() => unregisterMutation.mutate(event.id), event.id)}
                    guestLabel={!isAuthed}
                    onDetail={() => navigate(appPath(`/events/${event.id}`))}
                  />
                ),
              )}
            </div>
          </section>
        )}

        {futureEvents.length > 0 && (
          <section>
            <h2 className="text-lg font-bold mb-4">Предстоящие</h2>
            <div className="space-y-4">
              {futureEvents.map((event, i) =>
                playEventsStagger ? (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (todayEvents.length + i) * 0.04 }}
                  >
                    <EventCard
                      event={event}
                      isRegistered={registeredIds.has(event.id)}
                      isPending={
                        (registerMutation.isPending && registerMutation.variables === event.id) ||
                        (unregisterMutation.isPending && unregisterMutation.variables === event.id)
                      }
                      onRegister={() => requireAuthThen(() => registerMutation.mutate(event.id), event.id)}
                      onUnregister={() => requireAuthThen(() => unregisterMutation.mutate(event.id), event.id)}
                      guestLabel={!isAuthed}
                      onDetail={() => navigate(appPath(`/events/${event.id}`))}
                    />
                  </motion.div>
                ) : (
                  <EventCard
                    key={event.id}
                    event={event}
                    isRegistered={registeredIds.has(event.id)}
                    isPending={
                      (registerMutation.isPending && registerMutation.variables === event.id) ||
                      (unregisterMutation.isPending && unregisterMutation.variables === event.id)
                    }
                    onRegister={() => requireAuthThen(() => registerMutation.mutate(event.id), event.id)}
                    onUnregister={() => requireAuthThen(() => unregisterMutation.mutate(event.id), event.id)}
                    guestLabel={!isAuthed}
                    onDetail={() => navigate(appPath(`/events/${event.id}`))}
                  />
                ),
              )}
            </div>
          </section>
        )}

        {events.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-black/30">
            <p className="text-base font-medium">Событий пока нет</p>
          </div>
        )}
      </div>

      <AuthPromptModal
        open={authPromptOpen}
        onClose={closeAuthPrompt}
        onConfirm={confirmAuthPrompt}
      />
    </div>
  );
}

interface EventCardProps {
  event: Event;
  isRegistered: boolean;
  isPending: boolean;
  guestLabel?: boolean;
  onRegister: () => void;
  onUnregister: () => void;
  onDetail: (eventId: string) => void;
}

function EventCard({ event, isRegistered, isPending, guestLabel, onRegister, onUnregister, onDetail }: EventCardProps) {
  const dateObj = new Date(event.date);
  const formattedDate = dateObj.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

  return (
    <div
      className="rounded-3xl overflow-hidden"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      {event.image && (
        <div className="h-40 w-full relative">
          <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
          <div
            className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold"
            style={{ background: 'rgba(10,10,10,0.75)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
          >
            {formattedDate}
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start justify-between mb-1">
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1" style={{ color: '#fff' }}>{event.title}</h3>
            {event.description && (
              <p className="text-sm mb-3 line-clamp-2" style={{ color: 'rgba(255,255,255,0.45)' }}>{event.description}</p>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDetail(event.id);
            }}
            className="p-2 rounded-full active:scale-90 transition-all shrink-0 ml-2"
            style={{ background: 'rgba(255,255,255,0.08)', color: '#fff' }}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {event.time && (
            <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <Clock size={13} />
              {event.time}
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <MapPin size={13} />
              {event.location}
            </div>
          )}
          {event.registrationsCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <Users size={13} />
              {event.registrationsCount}
            </div>
          )}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            isRegistered ? onUnregister() : onRegister();
          }}
          disabled={isPending}
          className="w-full py-3 rounded-2xl text-sm font-semibold transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 tracking-[0.06em] uppercase"
          style={
            isRegistered
              ? { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', fontFamily: 'Manrope, sans-serif' }
              : { background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }
          }
        >
          {isPending ? (
            <Loader2 size={15} className="animate-spin" />
          ) : isRegistered ? (
            'Отменить запись'
          ) : guestLabel ? (
            'Войти и записаться'
          ) : (
            'Записаться'
          )}
        </button>
      </div>
    </div>
  );
}
